/**
 * useWorkspaces.js
 *
 * React binding of the workspace store (`workspaceStore.js`) wired to the real IO:
 * the persistent local mirror (offline cache) and the account endpoints
 * (`GET`/`PUT /api/user/me/dashboard-config`, per-namespace merge on the backend).
 *
 * One store per app process: the Manager tab, the editor sheets and the entry
 * screen all share it, so a rename in a sheet shows up in the tab strip instantly.
 *
 * @module helpers/workspaces/useWorkspaces
 */

import {useCallback, useEffect, useMemo, useSyncExternalStore} from 'react';
import {getSettingJSON, setSetting} from '../persistentSettings';
import {getMyDashboardConfig, saveMyDashboardConfigPatch} from '../../services/userApi';
import {createWorkspaceStore, WORKSPACES_CACHE_KEY} from './workspaceStore';
import * as boards from './workspaceModel';
import * as widgets from './workspaceWidgets';

let sharedStore = null;

/**
 * @returns {object} The process-wide store (created lazily with the real IO).
 */
export const getWorkspaceStore = () => {
    if (!sharedStore) {
        sharedStore = createWorkspaceStore({
            readMirror: () => getSettingJSON(WORKSPACES_CACHE_KEY, null),
            writeMirror: (value) => setSetting(WORKSPACES_CACHE_KEY, value),
            readRemote: (namespace) => getMyDashboardConfig(namespace),
            saveRemote: (patch) => saveMyDashboardConfigPatch(patch),
        });
    }

    return sharedStore;
};

/**
 * Workspace state plus the edit operations, each already optimistic + persisted.
 *
 * @returns {object} `{settled, enabled, state, activeBoard, saving, offline, enable, disable, ...operations}`.
 */
export const useWorkspaces = () => {
    const store = getWorkspaceStore();
    const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);

    useEffect(() => {
        store.ensureLoaded();

        // Do not sit on a pending debounced save when the screen goes away.
        return () => {
            store.flush();
        };
    }, [store]);

    const mutate = useCallback((transform) => store.mutate(transform), [store]);

    const activeBoard = useMemo(
        () => snapshot.state?.boards.find((board) => board.id === snapshot.state.activeId) || null,
        [snapshot.state],
    );

    return useMemo(() => ({
        settled: snapshot.ready && !snapshot.loading,
        loading: snapshot.loading,
        enabled: snapshot.enabled,
        state: snapshot.state,
        activeBoard,
        saving: snapshot.saving,
        offline: snapshot.offline,
        enable: (options) => store.enable(options),
        disable: () => store.disable(),
        reload: () => store.reload(),
        activate: (boardId) => store.activate(boardId),
        addBoard: (input) => {
            let result = null;

            mutate((state) => {
                result = boards.addBoard(state, input);

                return result.state;
            });

            return result;
        },
        renameBoard: (boardId, name) => {
            let result = null;

            mutate((state) => {
                result = boards.renameBoard(state, boardId, name);

                return result.state;
            });

            return result;
        },
        removeBoard: (boardId) => {
            let result = null;

            mutate((state) => {
                result = boards.removeBoard(state, boardId);

                return result.state;
            });

            return result;
        },
        duplicateBoard: (boardId, options) => {
            let result = null;

            mutate((state) => {
                result = boards.duplicateBoard(state, boardId, options);

                return result.state;
            });

            return result;
        },
        moveBoardBy: (boardId, direction) => mutate((state) => boards.moveBoardBy(state, boardId, direction)),
        setBoardPeriod: (boardId, period) => mutate((state) => boards.setBoardPeriod(state, boardId, period)),
        addWidget: (boardId, type, options) => {
            let result = null;

            mutate((state) => {
                result = widgets.addWidget(state, boardId, type, options);

                return result.state;
            });

            return result;
        },
        removeWidget: (boardId, widgetId) => mutate((state) => widgets.removeWidget(state, boardId, widgetId)),
        moveWidgetBy: (boardId, widgetId, direction) => mutate((state) => widgets.moveWidgetBy(state, boardId, widgetId, direction)),
        reorderWidgets: (boardId, from, to) => mutate((state) => widgets.reorderWidgets(state, boardId, from, to)),
        resizeWidget: (boardId, widgetId, size) => mutate((state) => widgets.resizeWidget(state, boardId, widgetId, size)),
        setWidgetOptions: (boardId, widgetId, patch) => mutate((state) => widgets.setWidgetOptions(state, boardId, widgetId, patch)),
        setBoardWidgets: (boardId, list) => mutate((state) => widgets.setBoardWidgets(state, boardId, list)),
        applyTemplate: (template, options) => {
            let result = null;

            mutate((state) => {
                result = widgets.applyTemplateAsBoard(state, template, options);

                return result.state;
            });

            return result;
        },
    }), [snapshot, activeBoard, store, mutate]);
};

export default useWorkspaces;
